import { CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons'
import { Dropdown } from 'antd'
import classNames from 'classnames'
import { type CSSProperties, type FC, useState } from 'react'

import { Select } from 'common/components/Select/Select'
import {
  getScheduleStaffColor,
  getScheduleStaffInitials,
} from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'

import styles from './ehrAppointmentV2.module.scss'

export const PROVIDER_TEXT = {
  label: 'Appointment with',
  primary: 'Primary',
  makePrimary: 'Make primary',
  addProvider: 'Add provider',
  menuTitle: 'Select one or more',
  done: 'Done',
  addMore: 'Add more providers to make it a joint appointment (one visit, several providers).',
  jointEdit:
    'Changes here update the whole visit: every provider’s copy in OpenEMR is added, removed or updated.',
  required: 'Pick at least one provider.',
} as const

export type PersonOption = { id: number; name: string; role: string; color: string }

type Props = {
  // OpenEMR books several people on one visit; other sites book one provider.
  multiple: boolean
  options: PersonOption[]
  people: number[]
  // The row's own doctor, who stays on an edited visit.
  lockedId: number | null
  primaryId: number | null
  canMakePrimary: boolean
  isEditing?: boolean
  disabled?: boolean
  onChange: (people: number[]) => void
  onMakePrimary: (id: number) => void
}

const countOf = (count: number): string =>
  count === 0 ? '' : count > 1 ? `${count} providers · joint appointment` : '1 provider'

// Initials and color as the doctor rail shows them.
const Avatar: FC<{ person: PersonOption }> = ({ person }) => (
  <span
    className={styles.providerAvatar}
    style={{ '--staff-color': person.color } as CSSProperties}
    aria-hidden>
    {getScheduleStaffInitials(person.name)}
  </span>
)

export const ProviderField: FC<Props> = ({
  multiple,
  options,
  people,
  lockedId,
  primaryId,
  canMakePrimary,
  isEditing = false,
  disabled,
  onChange,
  onMakePrimary,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const personOf = (id: number): PersonOption =>
    options.find((option) => option.id === id) ?? {
      id,
      name: `Provider #${id}`,
      role: '',
      color: getScheduleStaffColor(id),
    }

  if (!multiple) {
    return (
      <div className={styles.band}>
        <div className={styles.widthProvider}>
          <Select
            label={PROVIDER_TEXT.label}
            value={people[0]}
            showSearch={false}
            disabled={disabled}
            options={options.map((option) => ({ value: option.id, label: option.name }))}
            onChange={(next: number) => onChange([next])}
          />
        </div>
      </div>
    )
  }

  const isJoint = people.length > 1
  const toggle = (id: number) =>
    onChange(people.includes(id) ? people.filter((person) => person !== id) : [...people, id])

  const menu = (
    <div
      className={styles.providerMenu}
      role='listbox'
      aria-multiselectable
      aria-label={PROVIDER_TEXT.label}>
      <div className={styles.providerMenuTitle}>{PROVIDER_TEXT.menuTitle}</div>
      {options.map((option) => {
        const isOn = people.includes(option.id)
        return (
          <button
            key={option.id}
            type='button'
            role='option'
            aria-selected={isOn}
            className={styles.providerMenuOption}
            disabled={option.id === lockedId}
            onClick={() => toggle(option.id)}>
            <span className={classNames(styles.providerCheck, { [styles.providerCheckOn]: isOn })}>
              {isOn && <CheckOutlined aria-hidden />}
            </span>
            <Avatar person={option} />
            <span className={styles.providerMenuName}>{option.name}</span>
            <span className={styles.providerMuted}>{option.role}</span>
          </button>
        )
      })}
      <div className={styles.providerMenuDivider} />
      <div className={styles.providerMenuFooter}>
        <span className={styles.providerMuted}>{people.length} selected</span>
        <button type='button' className={styles.providerDone} onClick={() => setIsMenuOpen(false)}>
          {PROVIDER_TEXT.done}
        </button>
      </div>
    </div>
  )

  return (
    <section
      className={classNames(styles.providerBand, {
        [styles.providerBandJoint]: isJoint,
        [styles.providerBandEmpty]: people.length === 0,
      })}
      aria-label={PROVIDER_TEXT.label}>
      <div className={styles.providerHeader}>
        <span className={styles.providerLabel}>
          {PROVIDER_TEXT.label} <span className={styles.providerRequired}>*</span>
        </span>
        <span className={styles.providerMuted}>{countOf(people.length)}</span>
      </div>

      {/* Only the Add provider button opens the menu; a click elsewhere closes it. */}
      <Dropdown
        open={isMenuOpen && !disabled}
        trigger={['click']}
        placement='bottomLeft'
        align={{ offset: [0, 8] }}
        popupRender={() => menu}
        onOpenChange={(next) => {
          if (!next) setIsMenuOpen(false)
        }}>
        <div className={styles.providerChips}>
          {people.map((id) => {
            const person = personOf(id)
            const { name } = person
            const isPrimary = isJoint && id === primaryId
            return (
              <span
                key={id}
                className={classNames(styles.providerChip, {
                  [styles.providerChipPrimary]: isPrimary,
                })}
                data-testid={`provider-chip-${name}`}>
                <Avatar person={person} />
                <span className={styles.providerName}>{name}</span>
                {isPrimary && (
                  <span className={styles.providerPrimary}>{PROVIDER_TEXT.primary}</span>
                )}
                {canMakePrimary && !isPrimary && !disabled && (
                  <button
                    type='button'
                    className={styles.providerLink}
                    aria-label={`${PROVIDER_TEXT.makePrimary}: ${name}`}
                    onClick={() => onMakePrimary(id)}>
                    {PROVIDER_TEXT.makePrimary}
                  </button>
                )}
                {id !== lockedId && !disabled && (
                  <button
                    type='button'
                    className={styles.providerRemove}
                    aria-label={`Remove ${name}`}
                    onClick={() => onChange(people.filter((person) => person !== id))}>
                    <CloseOutlined aria-hidden />
                  </button>
                )}
              </span>
            )
          })}
          {!disabled && (
            <button
              type='button'
              className={styles.providerAdd}
              aria-haspopup='listbox'
              aria-expanded={isMenuOpen}
              onClick={() => setIsMenuOpen(true)}>
              <PlusOutlined aria-hidden />
              {PROVIDER_TEXT.addProvider}
            </button>
          )}
        </div>
      </Dropdown>

      {people.length === 0 && (
        <span className={styles.providerError}>{PROVIDER_TEXT.required}</span>
      )}
      {people.length === 1 && !isEditing && (
        <span className={styles.providerMuted}>{PROVIDER_TEXT.addMore}</span>
      )}
      {isEditing && isJoint && (
        <span className={styles.providerMuted}>{PROVIDER_TEXT.jointEdit}</span>
      )}
    </section>
  )
}
